import { clsx, type ClassValue } from 'clsx';

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

/**
 * Formats a monetary amount in cents to a dollar string with proper comma formatting.
 * Examples:
 *   formatMoney(1000) => "$10.00"
 *   formatMoney(100000) => "$1,000.00"
 *   formatMoney(-5000) => "-$50.00"
 */
export function formatMoney(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  const dollars = abs / 100;

  const formatted = dollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${sign}$${formatted}`;
}

/**
 * Formats a monetary amount in cents to a dollar string,
 * always showing the absolute value (no negative sign).
 */
export function formatMoneyAbsolute(cents: number): string {
  const abs = Math.abs(cents);
  const dollars = abs / 100;

  const formatted = dollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `$${formatted}`;
}

/**
 * Map expense category ID to icon name for react-icons/fi
 */
export function getCategoryIcon(categoryId: string): string {
  const iconMap: Record<string, string> = {
    general: 'FiGrid',
    school: 'FiBook',
    medical: 'FiHeart',
    clothing: 'FiShoppingBag',
    activities: 'FiActivity',
    groceries: 'FiShoppingCart',
    transportation: 'FiTruck',
    childcare: 'FiUsers',
    entertainment: 'FiFilm',
    other: 'FiMoreHorizontal',
  };
  return iconMap[categoryId] ?? 'FiGrid';
}

/**
 * Map payment method ID to icon name
 */
export function getPaymentMethodIcon(paymentMethodId: string): string {
  const iconMap: Record<string, string> = {
    venmo: 'FiCreditCard',
    zelle: 'FiDollarSign',
    paypal: 'FiCreditCard',
    cash: 'FiDollarSign',
    check: 'FiFileText',
    bank_transfer: 'FiDollarSign',
    apple_pay: 'FiSmartphone',
    cashapp: 'FiCreditCard',
    other: 'FiMoreHorizontal',
  };
  return iconMap[paymentMethodId] ?? 'FiCreditCard';
}

/**
 * Currency input validation - checks if string is valid currency format
 */
export function isValidCurrencyInput(value: string): boolean {
  return /^\$?\d*\.?\d{0,2}$/.test(value);
}

/**
 * Convert dollar string to cents
 */
export function dollarsToCents(input: string): number | null {
  const normalized = input.replace(/[$,]/g, '');
  if (!normalized) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
}

/**
 * Format date for display (e.g., "Jan 15")
 */
export function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format date for input (YYYY-MM-DD)
 */
export function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
